import * as React from 'react';
import {useState, useEffect, useRef} from "react";
import {
    Grid,
    TextField,
    Button,
    CircularProgress,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    ImageList,
    ImageListItem,
    Avatar,
    Box,
    Skeleton
} from '@mui/material';
import {useParams} from 'react-router-dom';
import {useNavigate, useLocation} from 'react-router-dom'
import Typography from '@mui/material/Typography';
import axios from 'axios';
import {getLocalToken, logout} from "../../utils";
import ActivatePlanDialog from "./activatePlanDialog";
import VisibilityIcon from '@mui/icons-material/Visibility';
import {PhotoProvider, PhotoView} from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import ImageUploader from './imageUploader';
import translations from '../../common/translation/translations';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';
import FinishPlanDialog from "./finishPlanDialog";

export default function UserPlanDetails() {
    const {state} = useLocation();
    const {id} = useParams();
    const [mainUserUid, setMainUserUid] = useState(state ? state.userUid : '');
    const [fname, setFname] = useState(state ? state.firstName : '');
    const [lname, setLname] = useState(state ? state.lastName : '');
    const [email, setEmail] = useState(state ? state.email : '');
    const [phoneNumber, setPhoneNumber] = useState(state ? state.phoneNumber : '');
    const [socialNumber, setSocialNumber] = useState(state ? state.socialNumber : '');
    const [dateOfBirth, setDateOfBirth] = useState(state ? state.dateOfBirth : '');
    const [showFinishPlanDialog, setShowFinishPlanDialog] = useState(false);
    const [status, setStatus] = useState(state ? state.status : '');
    const [totalPrice, setTotalPrice] = useState(state ? state.totalPrice : '');
    const [expiresAt, setExpiresAt] = useState(state ? state.expiresAt : '');
    const [userAvatar, setUserAvatar] = useState(state ? state.avatar : {});
    const [planSnapshot, setPlanSnapshot] = useState({
        logo: null,
        insurer: 'N/A',
        option: 'N/A',
        franchise: 'N/A',
        ageGroup: 'N/A'
    });

    const [serverError, setServerError] = useState('');
    const [loading, setLoading] = useState(false);
    const [filesLoading, setFilesLoading] = useState(false);

    const [showActivatePlanDialog, setShowActivatePlanDialog] = useState(false);
    const [planStatusUpdated, setPlanStatusUpdated] = useState(false);

    const [showFiles, setShowFiles] = useState(false);
    const [files, setFiles] = useState([]);

    const [familyMembers, setFamilyMembers] = useState([]);
    const [familyMembersLoading, setFamilyMembersLoading] = useState(false);

    const [uploadingImg, setUploadingImg] = useState(false);
    const [uploadError, setUploadError] = useState('');

    const navigate = useNavigate();
    const token = getLocalToken();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setServerError('');
            try {
                const res = await axios.get(`/admin/api/v1/users/plans/${id}`, {
                    headers: {"Authorization": `Bearer ${token}`}
                });
                if (res.status === 200 && res.data.payload) {
                    const data = res.data.payload;

                    setFname(data.firstName);
                    setLname(data.lastName);
                    setEmail(data.email);
                    setPhoneNumber(data.phoneNumber);
                    setSocialNumber(data.socialNumber);
                    setStatus(data.status);
                    setTotalPrice(data.totalPrice);
                    setExpiresAt(data.expiresAt);

                    setPlanSnapshot({
                        logo: data?.snapshot?.logo || null,
                        insurer: data?.snapshot?.insurer_name || 'N/A',
                        option: data?.snapshot?.tariff_name || 'N/A',
                        franchise: data?.snapshot?.franchise_code || 'N/A',
                        ageGroup: data?.snapshot?.age_group_code || 'N/A'
                    });
                    setUserAvatar(data.avatar);
                    setDateOfBirth(data.dateOfBirth);
                    setMainUserUid(data.userUid);

                    await fetchUserFamily(data.userUid);
                }
            } catch (err) {
                console.log(err);
                if (err.response?.status === 401) {
                    logout();
                    navigate('/login');
                } else {
                    setServerError('Failed to load user details.');
                }
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [planStatusUpdated]);

    const fetchUserFamily = (userUID) => {
        setFamilyMembersLoading(true);
        axios.get(`/admin/api/v1/users/${userUID}/family`, {headers: {"Authorization": `Bearer ${token}`}})
            .then((res) => {
                if (res.status === 200 && res.data.payload) {
                    setFamilyMembers(res.data.payload);
                } else {
                    setFamilyMembers([]);
                }
            })
            .catch((err) => {
                setServerError('Failed to load family members.');
                setFamilyMembers([]);
            })
            .finally(() => {
                setFilesLoading(false);
            });
    };

    const fetchPassportFiles = () => {
        setShowFiles(true);
        setFilesLoading(true);
        setServerError('');
        axios.get(`/admin/api/v1/users/${mainUserUid}/files`, {headers: {"Authorization": `Bearer ${token}`}})
            .then((res) => {
                if (res.status === 200 && res.data.payload) {
                    console.log(res.data.payload)
                    setFiles(res.data.payload);
                } else {
                    setFiles([]);
                }
            })
            .catch((err) => {
                setServerError('Failed to load passport files.');
                setFiles([]);
            })
            .finally(() => {
                setFilesLoading(false);
            });
    };

    let actionBtns = <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
            variant="contained"
            size="small"
            color="success"
            disabled={status && status != 'active' && status != 'canceled' && status != 'expired' ? false : true}
            onClick={() => setShowActivatePlanDialog(true)}
            sx={{
                minWidth: '22px',
                padding: '4px 8px',
                textTransform: 'none'
            }}
        >
            Aktivieren
        </Button>

        <Button
            variant="contained"
            size="small"
            color="error"
            disabled={status === 'expired' || status === 'canceled'}
            onClick={() => setShowFinishPlanDialog(true)}
            sx={{
                minWidth: '22px',
                padding: '4px 8px',
                textTransform: 'none'
            }}
        >
            Beenden
        </Button>

        <Button
            variant="outlined"
            size="small"
            color="primary"
            disabled={(status === 'canceled' || status === 'expired') ? false : true}
            onClick={() => navigate(`/user-plans/new/${mainUserUid}/edit`)}
            sx={{ minWidth: '22px', padding: '4px 8px', textTransform: 'none' }}
        >
            Neuen Plan hinzufügen
        </Button>
    </Box>

    let filesGallery;
    if (showFiles) {
        if (filesLoading) {
            filesGallery = (
                <ImageList
                    sx={{
                        width: '100%',
                        height: 'auto',
                    }}
                    cols={6}
                    gap={8}
                >
                    {Array.from(new Array(6)).map((_, index) => (
                        <Skeleton
                            key={index}
                            variant="rectangular"
                            width="100%"
                            height={120}
                            sx={{borderRadius: 2}}
                        />
                    ))}
                </ImageList>
            );
        } else if (files.length === 0) {
            filesGallery = (
                <Typography variant="body2" color="error" sx={{mt: 2}}>
                    Keine Passdateien hochgeladen.
                </Typography>
            );
        } else {
            filesGallery = (
                <PhotoProvider>
                    <ImageList
                        sx={{
                            width: '40%',
                            height: 'auto',
                        }}
                        cols={6}
                        gap={8}
                    >
                        {files.map((url, index) => (
                            <ImageListItem
                                key={index}
                                sx={{
                                    cursor: 'pointer',
                                    overflow: 'hidden',
                                    borderRadius: 2
                                }}
                            >
                                <PhotoView src={url}>
                                    <img
                                        src={url}
                                        alt={`passport-${index}`}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            borderRadius: '8px',
                                            transition: '0.3s',
                                        }}
                                    />
                                </PhotoView>
                            </ImageListItem>
                        ))}
                    </ImageList>
                </PhotoProvider>
            );
        }
    }

    const familyTbl = (
        <>
            <Typography variant="h5" gutterBottom style={{color: '#1d1d1d'}}>
                Familienmitglieder
            </Typography>
            <Table>
                <TableHead>
                    <TableRow sx={{backgroundColor: 'rgba(0, 0, 0, 0.05)'}}>
                        <TableCell>Name</TableCell>
                        <TableCell>Sozialversicherungsnummer</TableCell>
                        <TableCell>Geburtsdatum</TableCell>
                        <TableCell>Erstellt</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {familyMembers && familyMembers.length > 0 ? (
                        familyMembers.map((member, index) => (
                            <TableRow key={index}>
                                <TableCell>{member.firstName} {member.lastName}</TableCell>
                                <TableCell>{member.socialNumber}</TableCell>
                                <TableCell>{member.dateOfBirth}</TableCell>
                                <TableCell>{member.created}</TableCell>
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={3} align="center">
                                Keine Familienmitglieder gefunden.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </>
    );

    const handleUpload = async (files) => {
        setUploadingImg(true);
        setUploadError('');
        try {
            const formData = new FormData();
            formData.append('userUID', mainUserUid);
            formData.append('planUID', id);
            files.forEach(file => formData.append('attachments', file));

            const res = await axios.post( `/admin/api/v1/users/${mainUserUid}/files`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': `Bearer ${token}`,
                },
            });
            fetchPassportFiles();
            return true; // signals ImageUploader to clear images
        } catch (err) {
            console.error('Upload failed', err);
            setUploadError('Upload failed. Please try again.');
            return true;
        } finally {
            setUploadingImg(false);
        }
    };

    return (
        <div>
            <Grid
                container
                spacing={2}
                alignItems="flex-start"
                justifyContent="space-between"
            >
                <Grid item xs={12} md={6}>
                    <Box>
                        <Grid item xs={4}>
                            <Typography
                                variant="h5"
                                gutterBottom
                                sx={{
                                    color: '#1d1d1d',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                <Avatar
                                    src={userAvatar}
                                    alt="Benutzer-Avatar"
                                    sx={{ width: 50, height: 50, mr: 2 }}
                                />
                                {fname} {lname}

                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => navigate(`/users/${mainUserUid}/edit`)}
                                    sx={{ ml: 1 }}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </Typography>
                        </Grid>
                        <TextField
                            name="email"
                            type="email"
                            variant="standard"
                            fullWidth
                            id="email"
                            label="Email"
                            value={email || ''}
                            sx={{m: 1}}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                        <TextField
                            name="phone"
                            type="text"
                            variant="standard"
                            fullWidth
                            id="phone"
                            label="Telefon"
                            value={phoneNumber || ''}
                            sx={{m: 1}}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                        <TextField
                            name="socialNumber"
                            type="text"
                            variant="standard"
                            fullWidth
                            id="socialNumber"
                            label="Sozialnummer"
                            value={socialNumber || ''}
                            sx={{m: 1}}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                        <TextField
                            name="dob"
                            type="dob"
                            variant="standard"
                            fullWidth
                            id="dob"
                            label="Geburtsdatum"
                            value={dateOfBirth || ''}
                            sx={{m: 1}}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                        <br/> <br/>
                        <div style={{marginLeft: '5px'}}>
                            <ImageUploader
                                onChange={(selectedFiles) => console.log(selectedFiles)}
                                onUpload={handleUpload}
                                loading={uploadingImg}
                                error={!!uploadError}
                                helperText={uploadError}
                            />
                        </div>
                    </Box>
                </Grid>
            </Grid>

            <br/>
            <Button
                variant="contained"
                size="small"
                startIcon={filesLoading ? <CircularProgress color="inherit" size={20}/> : <VisibilityIcon/>}
                onClick={fetchPassportFiles}
                disabled={filesLoading || files.length > 0}
                sx={{textTransform: 'none', m: 1, borderRadius: 1}}
            >
                {filesLoading ? 'Laden...' : 'Angehängte Fotos anzeigen'}
            </Button>
            <br/><br/>

            {filesGallery}
            <Grid item xs={4}>
                <Typography variant="h5" gutterBottom style={{color: '#1d1d1d'}}>
                    Ausgewählte Plandetails
                </Typography>
            </Grid>
            <Table>
                <TableHead>
                    <TableRow sx={{backgroundColor: `rgba(0, 0, 0, 0.05)`}}>
                        <TableCell></TableCell>
                        <TableCell>Versicherer</TableCell>
                        <TableCell>Option</TableCell>
                        <TableCell>Franchise</TableCell>
                        <TableCell>Altersgruppe</TableCell>
                        <TableCell>Gültig bis</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Gesamtpreis</TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                        <TableCell>  {planSnapshot.logo && (
                            <Avatar
                                src={planSnapshot.logo}
                                alt={planSnapshot.logo}
                                variant="square"
                                sx={{width: 40, height: 40, mr: 2}}
                            />
                        )}</TableCell>
                        <TableCell>{planSnapshot.insurer}</TableCell>
                        <TableCell>{planSnapshot.option}</TableCell>
                        <TableCell>{planSnapshot.franchise}</TableCell>
                        <TableCell>{planSnapshot.ageGroup}</TableCell>
                        <TableCell>{expiresAt}</TableCell>
                        <TableCell
                            style={
                                status?.toLowerCase() === 'active'
                                    ? { color: 'green', fontWeight: 'bold' }
                                    : {}
                            }
                        >
                            {translations['de']?.statuses?.[status?.toLowerCase()] || status}
                        </TableCell>
                        <TableCell>{totalPrice}</TableCell>
                        <TableCell align="right">{actionBtns}</TableCell>
                    </TableRow>
                </TableBody>
            </Table>

            <br/>
            {familyTbl}
            <br/>

            <div style={{marginBottom: '20px'}}>{serverError}</div>
            <Grid container>
                <Grid item xs={6} style={{textAlign: 'right'}}>
                    <Button variant="outlined" onClick={() => {
                        navigate('/user-plans')
                    }}>
                        Zurück
                    </Button>
                </Grid>
            </Grid>

            <ActivatePlanDialog
                open={showActivatePlanDialog}
                onClose={(e) => {
                    setShowActivatePlanDialog(false)
                }}
                onSuccess={() => {
                    setShowActivatePlanDialog(false);
                    setPlanStatusUpdated(true)
                }}
                planUID={id}
                insurer={planSnapshot.insurer}
                planName={planSnapshot.option}
                token={token}
            />

            <FinishPlanDialog
                open={showFinishPlanDialog}
                onClose={() => setShowFinishPlanDialog(false)}
                onSuccess={() => {
                    setShowFinishPlanDialog(false);
                    setPlanStatusUpdated(!planStatusUpdated);
                }}
                planUID={id}
                insurer={planSnapshot.insurer}
                planName={planSnapshot.option}
                token={token}
            />

        </div>
    );
}